import { Component, computed, inject, signal, viewChild, viewChildren } from '@angular/core';
import { delay } from '@gametailors/web-core/misc';
import { RidButton } from '../../components/rid-button/rid-button';
import { CompressibleCardsService } from '../../components/rid-compressable-klank-card/compressible-cards.service';
import { RidCompressableKlankCard } from '../../components/rid-compressable-klank-card/rid-compressable-klank-card';
import { RidFunctionalSyllabicKeyboard } from '../../components/rid-functional-syllabic-keyboard/rid-functional-syllabic-keyboard';
import { RidMechanicBackground } from '../../components/rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../../components/rid-mechanic-content/rid-mechanic-content';
import { RidMechanicWrapper } from '../../components/rid-mechanic-wrapper/rid-mechanic-wrapper';
import {
  MORPHEME_SUFFIXES_B,
  RidMorphemeKeyboard,
  stripMorphemeDashes,
  TPrefixValue,
  TSuffixValue,
} from '../../components/rid-morpheme-keyboard/rid-morpheme-keyboard';
import { RidOverlayFeedback } from '../../components/rid-overlay-feedback/rid-overlay-feedback';
import { RidSyllabicCard } from '../../components/rid-syllabic-card/rid-syllabic-card';
import { RidTextInput } from '../../components/rid-text-input/rid-text-input';
import { RidTopBar } from '../../components/rid-top-bar/rid-top-bar';
import { RidVerticalSpacer } from '../../components/rid-vertical-spacer/rid-vertical-spacer';
import { RidSfxDirective } from '../../directives/rid-sfx.directive';
import { IconBackspace } from '../../icons/i-backspace';
import { IconEnter } from '../../icons/i-enter';
import { PronunciationService } from '../../services/pronunciation.service';

// Unlike the assignments, rules are typed by the instructor themselves.
const CENTER_LAYOUT = {
  KLINKERS: 'ENABLED',
  LANGE_KLINKERS: 'ENABLED',
  KORTE_LANGE_KLINKERS: 'ENABLED',
  SPACE: 'HIDDEN',
  DUBBELE_KLINKERS: 'ENABLED',
  DRIEDUBBELE_KLINKERS: 'ENABLED',
  VIERDUBBELE_KLINKERS: 'ENABLED',
  MEDEKLINKERS: 'ENABLED',
  EXOTEN: 'HIDDEN',
  SJWAS: 'HIDDEN',
  KLANKVOET: 'HIDDEN',
  RULE: 'ENABLED',
} as const;

/**
 * Instruction mode of the morpheme board: no prompts. The instructor picks a prefix and/or suffix, continues,
 * and then types the stem freely, after which the whole word is pronounced.
 */
@Component({
  selector: 'rid-morpheme-board-freeform',
  imports: [
    RidMechanicBackground,
    RidMechanicWrapper,
    RidTopBar,
    RidMechanicContent,
    RidTextInput,
    RidVerticalSpacer,
    RidCompressableKlankCard,
    RidSyllabicCard,
    RidOverlayFeedback,
    RidFunctionalSyllabicKeyboard,
    RidMorphemeKeyboard,
    RidButton,
    IconBackspace,
    IconEnter,
    RidSfxDirective,
  ],
  templateUrl: './rid-morpheme-board-freeform.html',
  host: {
    class: 'block',
  },
})
export class RidMorphemeBoardFreeform {
  private readonly $pronunciationService = inject(PronunciationService);
  private readonly $compressibleCardsService = inject(CompressibleCardsService);

  protected readonly centerLayout = CENTER_LAYOUT;
  protected readonly stripDashes = stripMorphemeDashes;

  keyboardComp = viewChild.required<RidFunctionalSyllabicKeyboard>('keyboardComp');
  syllabicCard = viewChildren<RidSyllabicCard>('syllabicCard');
  prefixCard = viewChild<RidCompressableKlankCard>('prefixCard');
  suffixCard = viewChild<RidCompressableKlankCard>('suffixCard');

  protected step = signal<'MORPHEMES' | 'CENTER'>('MORPHEMES');
  protected prefix = signal<TPrefixValue | undefined>(undefined);
  protected suffix = signal<TSuffixValue | undefined>(undefined);
  protected isEvaluating = signal(false);
  protected version = signal(0);

  // Group B suffixes change the center (e.g. slaap -> slapen), which is signalled by colouring the center input the same way.
  protected isStemChangingSuffix = computed(() => {
    const suffix = this.suffix();
    return suffix !== undefined && MORPHEME_SUFFIXES_B.includes(suffix);
  });

  protected suffixCardClass = computed(() => (this.isStemChangingSuffix() ? 'font-bold text-morpheme-stem' : 'font-bold text-blue-500'));

  // Pressing the selected morpheme again deselects it.
  protected onPrefixPressed(value: TPrefixValue) {
    this.prefix.update((current) => (current === value ? undefined : value));
  }

  protected onSuffixPressed(value: TSuffixValue) {
    this.suffix.update((current) => (current === value ? undefined : value));
  }

  protected continue() {
    this.step.set('CENTER');
  }

  // Backspacing past the start of the stem returns to choosing morphemes.
  protected backspace() {
    const keyboard = this.keyboardComp();

    if (keyboard.isEmpty()) {
      this.step.set('MORPHEMES');
      return;
    }

    keyboard.pop();
  }

  protected async okay() {
    this.isEvaluating.set(true);

    const keyboard = this.keyboardComp();
    const isCompressible = keyboard.content().every((k) => k.type === 'KLANK' || k.type === 'EXCEPTION' || k.type === 'SPACE');
    if (isCompressible) {
      const prefixCard = this.prefixCard();
      const suffixCard = this.suffixCard();
      const cards = [
        ...(prefixCard ? [prefixCard] : []),
        ...this.syllabicCard().flatMap((c) => c.compressable()),
        ...(suffixCard ? [suffixCard] : []),
      ];
      this.$compressibleCardsService.compress(cards);
    }

    const prefix = this.prefix();
    const suffix = this.suffix();
    await this.$pronunciationService.tryPronounce(
      (prefix ? this.stripDashes(prefix) : '') + keyboard.getRawText() + (suffix ? this.stripDashes(suffix) : ''),
    );

    await delay(500);

    // Bumping the version replaces the whole board (including the keyboard), fading out the old one.
    this.prefix.set(undefined);
    this.suffix.set(undefined);
    this.step.set('MORPHEMES');
    this.version.update((v) => v + 1);
    this.isEvaluating.set(false);
  }
}
